import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdownTimer } from '@Hooks/useTimer';
import { bugsnagClient } from '@Services/observability/initObservability';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';

export interface SocureData {
  qrCode?: string;
  url?: string;
}

export interface SocureBaseLogicResult {
  openResendOption: boolean;
  setOpenResendOption: (value: boolean) => void;
  isSuccessNotificationVisible: boolean;
  setIsSuccessNotificationVisible: (value: boolean) => void;
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
  timeLeft: string | null;
  onSendSmsClick: () => Promise<void>;
  isLoadingSms: boolean;
  startTimer: () => void;
}

interface UseSocureBaseLogicOptions<T> {
  countdownDuration?: number;
  resendDocVFn: (options?: RefetchOptions) => Promise<QueryObserverResult<T>>;
  isSending: boolean;
}

export const useSocureBaseLogic = <T>(
  options: UseSocureBaseLogicOptions<T>
): SocureBaseLogicResult => {
  const { countdownDuration = 30, resendDocVFn, isSending } = options;
  const { t } = useTranslation('nonUsSocure');

  const [openResendOption, setOpenResendOption] = useState(false);
  const [isSuccessNotificationVisible, setIsSuccessNotificationVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const { startTimer, padTimeLeft } = useCountdownTimer({
    intervalDuration: countdownDuration,
    onFinishCountdown: () => {
      setOpenResendOption(true);
    },
  });

  const onSendSmsClick = useCallback(async () => {
    try {
      await resendDocVFn();
      startTimer();
      setOpenResendOption(false);
      setIsSuccessNotificationVisible(true);
    } catch (e) {
      setErrorMessage(t('mainContent.sendFailedError'));
      bugsnagClient?.notify(e as Error);
    }
  }, [resendDocVFn, startTimer, t]);

  return {
    openResendOption,
    setOpenResendOption,
    isSuccessNotificationVisible,
    setIsSuccessNotificationVisible,
    errorMessage,
    setErrorMessage,
    timeLeft: padTimeLeft,
    onSendSmsClick,
    isLoadingSms: isSending,
    startTimer,
  };
};
